<!--
  Settings → Dependency Sync, the two SAGA robots (SagaX1n invoices, SagaX2n
  productions): the "Test" and "Documents" tabs of their detail.

  Test: pick ONE invoice or production, see exactly what the robot would type
  into SAGA (the ERP builds it: api/app/helpers/sagaDocs.js), then
    - Check in SAGA: SAGA checks it, nothing is written;
    - Send to SAGA: typed in, NOT validated (the accountant validates).
  The robot on the VPS picks the test up within a minute; this panel follows it.

  Documents: what waits for the next run, what the robot could not send and
  why, the last documents it handled.

  Data: GET /api/sagasync/overview (passed in by the page), find, preview,
  requests, release (api/app/controllers/sagasync.controller.js).
-->
<template>
  <div class="saga-panel">
    <!-- ================= TEST ================= -->
    <div v-if="tab === 'test'" key="t">
      <p class="hv2-sec-sub">
        Pick one {{ kind }}. You see what {{ service.name }} would type into SAGA. Then check it in SAGA (nothing is written)
        or send it (typed in, <b>not validated</b>: the accountant validates). The robot looks every minute.
      </p>
      <div class="saga-find">
        <vs-input v-model="query" :placeholder="isInvoice ? 'Invoice number (RO2026_00812) or id' : 'Production name (2026_01073), PR01073 or id'"
                  class="saga-find-input" @keyup.enter="find" />
        <vs-button size="small" icon-pack="feather" icon="icon-search" :disabled="finding || !query.trim()" @click="find">Find</vs-button>
      </div>
      <div v-if="findError" class="hv2-error mt-2">{{ findError }}</div>
      <div v-if="candidates.length" class="saga-candidates">
        <div v-for="c in candidates" :key="c.id" class="saga-candidate" :class="{ 'is-selected': picked === c.id }"
             role="button" tabindex="0" @click="pick(c.id)" @keyup.enter="pick(c.id)">
          <b>{{ c.ref || `#${c.id}` }}</b>
          <span class="hv2-muted">{{ date(c.day) }} · #{{ c.id }}</span>
          <hv-pill v-if="c.issyncsaga" tone="info" label="In SAGA" />
          <hv-pill v-else-if="c.isactive === false" tone="" label="Cancelled" />
        </div>
      </div>

      <div v-if="previewLoading" class="hv2-loading mt-base"><vs-progress indeterminate color="primary" class="mb-2" />Reading…</div>
      <div v-else-if="previewError" class="hv2-error mt-base">{{ previewError }}</div>
      <div v-else-if="preview" class="mt-base">
        <div class="saga-doc-head">
          <div>
            <a :href="opHref(preview.document.idoperation)" target="_blank" class="saga-doc-ref">{{ preview.document.ref || `#${preview.document.idoperation}` }}</a>
            <span v-if="preview.document.name && preview.document.name !== preview.document.ref" class="hv2-muted"> ({{ preview.document.name }})</span>
            <span class="hv2-muted"> · {{ date(preview.document.date) }} · #{{ preview.document.idoperation }}</span>
          </div>
          <hv-pill v-if="inSaga" tone="info" :label="preview.operation.saga_sent_at ? `In SAGA since ${date(preview.operation.saga_sent_at)}` : 'In SAGA'" icon="CheckCircleIcon" />
          <hv-pill v-else-if="preview.document.held" tone="warn" label="Waiting confirmation" title="Customer confirmed by hand: the run leaves it until &quot;Ready to be sent to SAGA&quot; is clicked on the invoice. A Send from here confirms it." />
          <hv-pill v-else tone="" label="Not in SAGA" />
        </div>
        <div v-if="inSaga && preview.operation.saga_ref" class="hv2-muted mb-2">SAGA: {{ preview.operation.saga_ref }}</div>

        <div v-if="!preview.document.ok" class="hv2-verdict is-danger"><b>The robot would not send it.</b> {{ preview.document.reason }}</div>

        <!-- A production: its bons -->
        <template v-else-if="!isInvoice">
          <hv-table :rows="bonRows" row-key="key">
            <template slot="thead">
              <vs-th></vs-th><vs-th>SAGA code</vs-th><vs-th>m³</vs-th><vs-th>Warehouse</vs-th><vs-th>Preset price</vs-th><vs-th>From the ERP</vs-th>
            </template>
            <template slot="row" slot-scope="{ r }">
              <vs-td>{{ r.out ? '−' : '+' }}</vs-td>
              <vs-td class="hv2-code" :class="{ 'saga-out': r.out }">{{ r.code }}</vs-td>
              <vs-td class="hv2-col-n">{{ r.quantity.toFixed(3) }}</vs-td>
              <vs-td>{{ warehouse(r.warehouse) }}</vs-td>
              <vs-td class="hv2-col-n">{{ r.out ? '' : (r.preset_price ? r.preset_price : 'set by SAGA') }}</vs-td>
              <vs-td class="hv2-muted">{{ (r.products || []).join(', ') }}</vs-td>
            </template>
          </hv-table>
          <p class="hv2-muted mt-2">+ = a bon (what is made, where it enters) · − = what it consumes, valued by SAGA when typed in.</p>
        </template>

        <!-- An invoice: header and lines, as in the XML -->
        <template v-else>
          <div class="vx-row">
            <div class="vx-col md:w-1/2 w-full">
              <div class="hv2-kv"><div class="hv2-kv-k">Customer</div><div class="hv2-kv-v">{{ h.ClientNume }} <span class="hv2-muted">· CIF {{ h.ClientCIF || DASH }}</span></div></div>
              <div class="hv2-kv"><div class="hv2-kv-k">Address</div><div class="hv2-kv-v">{{ h.ClientAdresa || DASH }}, {{ h.ClientLocalitate || DASH }}<template v-if="h.ClientJudet"> ({{ h.ClientJudet }})</template> · {{ h.ClientTara || 'no country' }}</div></div>
            </div>
            <div class="vx-col md:w-1/2 w-full">
              <div class="hv2-kv"><div class="hv2-kv-k">Invoice</div><div class="hv2-kv-v">{{ h.FacturaNumar }} of {{ h.FacturaData }} · {{ preview.document.currency }}</div></div>
              <div class="hv2-kv"><div class="hv2-kv-k">Total</div><div class="hv2-kv-v">{{ preview.document.totals.value }} + VAT {{ preview.document.totals.vat }} {{ preview.document.currency }}</div></div>
              <!-- What SAGA shows in "Informații suplimentare": the ANAF upload index, or a
                   note that the ERP has not sent it yet — the ERP is the only sender, SAGA
                   never sends to the ANAF (Chady, 2026-11-03). -->
              <div class="hv2-kv"><div class="hv2-kv-k">e-Factura</div><div class="hv2-kv-v">{{ h.FacturaInformatiiSuplimentare || DASH }}</div></div>
            </div>
          </div>
          <hv-table :rows="lineRows" row-key="LinieNrCrt" class="mt-2">
            <template slot="thead">
              <vs-th>#</vs-th><vs-th>Article / text</vs-th><vs-th>UM</vs-th><vs-th>Qty</vs-th><vs-th>Price</vs-th><vs-th>Value</vs-th><vs-th>VAT</vs-th><vs-th>Account</vs-th><vs-th>Warehouse</vs-th>
            </template>
            <template slot="row" slot-scope="{ r }">
              <vs-td>{{ r.LinieNrCrt }}</vs-td>
              <vs-td><span v-if="r.CodArticolClient" class="hv2-code">{{ r.CodArticolClient }}</span><span v-else>{{ r.Descriere }}</span></vs-td>
              <vs-td>{{ r.UM }}</vs-td>
              <vs-td class="hv2-col-n">{{ r.Cantitate }}</vs-td>
              <vs-td class="hv2-col-n">{{ r.Pret }}</vs-td>
              <vs-td class="hv2-col-n">{{ r.Valoare }}</vs-td>
              <vs-td class="hv2-col-n">{{ r.TVA }} <span class="hv2-muted">({{ r.ProcTVA }} %)</span></vs-td>
              <vs-td>{{ r.Cont }}</vs-td>
              <vs-td>{{ r.Gestiune }}</vs-td>
            </template>
          </hv-table>
          <p class="hv2-muted mt-2">File {{ preview.document.file_name }}, imported through the SAGA "Import date" screen into Ieșiri{{ preview.document.currency === 'EUR' ? ' – valută' : '' }}.</p>
        </template>

        <ul v-if="preview.document.notes && preview.document.notes.length" class="saga-notes">
          <li v-for="(n, i) in preview.document.notes" :key="i">{{ n }}</li>
        </ul>

        <!-- The two actions, and the way back -->
        <div class="saga-actions">
          <vs-button size="small" type="border" icon-pack="feather" icon="icon-check-square"
                     :disabled="requestBusy || asking" @click="ask('check')">Check in SAGA</vs-button>
          <vs-button size="small" color="primary" icon-pack="feather" icon="icon-upload" class="ml-2"
                     :disabled="requestBusy || asking || inSaga || !preview.document.ok" @click="confirmSend">Send to SAGA</vs-button>
          <span v-if="inSaga" class="hv2-muted ml-3">Already in SAGA: the ERP will not send it twice.</span>
          <span v-else-if="preview.document.held" class="hv2-muted ml-3">Waiting confirmation: a Send from here counts as "Ready to be sent to SAGA".</span>
        </div>

        <div v-if="activeRequest" class="hv2-verdict mt-base" :class="requestTone(activeRequest) ? `is-${requestTone(activeRequest)}` : ''">
          <b>{{ requestTitle(activeRequest) }}</b>
          <div v-if="activeRequest.result && activeRequest.result.message">{{ activeRequest.result.message }}</div>
          <div v-for="it in activeRequest.items || []" :key="it.id" class="mt-1">
            <hv-pill :tone="itemTone(it.state)" :label="itemLabel(it.state)" /> {{ it.message }}
          </div>
        </div>

        <template v-if="inSaga">
          <div class="hv2-subhead mt-base">Release</div>
          <p class="hv2-sec-sub">Only after the {{ kind }} was deleted in SAGA. The ERP can then change it again, and the next run sends it again. Say why: it goes in the journal.</p>
          <div class="saga-find">
            <vs-input v-model="releaseReason" placeholder="Why (e.g. deleted in SAGA by the accountant, wrong date)" class="saga-find-input" />
            <vs-button size="small" color="danger" type="border" :disabled="releasing || releaseReason.trim().length < 5" @click="confirmRelease">Release</vs-button>
          </div>
        </template>

        <template v-if="preview.history.length">
          <div class="hv2-subhead mt-base">This {{ kind }} and SAGA</div>
          <div v-for="it in preview.history" :key="it.id" class="saga-history">
            <span class="hv2-muted hv2-nowrap">{{ roWhen(it.created_at, now) }}</span>
            <hv-pill :tone="itemTone(it.state)" :label="itemLabel(it.state)" />
            <span>{{ it.message }}</span>
          </div>
        </template>
      </div>

      <template v-if="overview && overview.requests.length">
        <div class="hv2-subhead mt-base">Last tests</div>
        <hv-table :rows="overview.requests" row-key="id" pagination :max-items="5">
          <template slot="thead">
            <vs-th>Asked</vs-th><vs-th>{{ isInvoice ? 'Invoice' : 'Production' }}</vs-th><vs-th>Test</vs-th><vs-th>By</vs-th><vs-th>Result</vs-th><vs-th>Detail</vs-th>
          </template>
          <template slot="row" slot-scope="{ r }">
            <vs-td class="hv2-nowrap">{{ roWhen(r.requested_at, now) }}</vs-td>
            <vs-td><a href="#" @click.prevent="pick(r.idoperation)">{{ r.ref || `#${r.idoperation}` }}</a></vs-td>
            <vs-td>{{ r.action === 'send' ? 'Send' : 'Check' }}</vs-td>
            <vs-td>{{ r.requested_by || DASH }}</vs-td>
            <vs-td><hv-pill :tone="requestTone(r)" :label="requestLabel(r)" /></vs-td>
            <vs-td>{{ (r.result && r.result.message) || '' }}</vs-td>
          </template>
        </hv-table>
      </template>
    </div>

    <!-- ================= DOCUMENTS ================= -->
    <div v-else-if="tab === 'documents'" key="d">
      <div v-if="!overview" class="hv2-muted">Not read yet.</div>
      <template v-else>
        <div class="vx-row">
          <div class="vx-col md:w-1/4 w-1/2 mb-3"><div class="hv2-kv-k">Waiting for the next run</div><div class="saga-count">{{ waitingRun.length }}</div></div>
          <!-- Customers of saga_hold_customer: the run leaves them until "Ready to be sent to SAGA" is clicked on the invoice. -->
          <div v-if="isInvoice" class="vx-col md:w-1/4 w-1/2 mb-3"><div class="hv2-kv-k">Waiting for confirmation</div><div class="saga-count">{{ waitingConfirm.length }}</div></div>
          <div class="vx-col md:w-1/4 w-1/2 mb-3"><div class="hv2-kv-k">Of today (go the night after)</div><div class="saga-count">{{ overview.today }}</div></div>
          <div class="vx-col md:w-1/4 w-1/2 mb-3"><div class="hv2-kv-k">Sent in 7 days</div><div class="saga-count">{{ overview.counts.sent_7d }}</div></div>
          <div class="vx-col md:w-1/4 w-1/2 mb-3"><div class="hv2-kv-k">Older than {{ overview.lookback_days }} days, not in SAGA</div><div class="saga-count">{{ overview.counts.older }}</div></div>
        </div>
        <p class="hv2-muted">The run sends what is dated before today and within the last {{ overview.lookback_days }} days (Settings). Older ones go only one by one, from the Test tab.</p>

        <div class="hv2-subhead mt-base">To look at</div>
        <hv-empty v-if="!overview.to_look_at.length" icon="CheckCircleIcon" title="Nothing to look at" :text="`Every ${kind} the robot tried went into SAGA.`" />
        <hv-table v-else :rows="overview.to_look_at" row-key="id" pagination :max-items="10">
          <template slot="thead">
            <vs-th>Date</vs-th><vs-th>{{ isInvoice ? 'Invoice' : 'Production' }}</vs-th><vs-th>State</vs-th><vs-th>Why</vs-th><vs-th></vs-th>
          </template>
          <template slot="row" slot-scope="{ r }">
            <vs-td>{{ date(r.day) }}</vs-td>
            <vs-td><a :href="opHref(r.id)" target="_blank">{{ r.ref || `#${r.id}` }}</a></vs-td>
            <vs-td><hv-pill :tone="itemTone(r.last.state)" :label="itemLabel(r.last.state)" /></vs-td>
            <vs-td>{{ r.last.message }}</vs-td>
            <vs-td><vs-button size="small" type="flat" @click="$emit('test', r.id)">Test</vs-button></vs-td>
          </template>
        </hv-table>

        <div class="hv2-subhead mt-base">Waiting</div>
        <hv-table :rows="overview.waiting" row-key="id" pagination :max-items="10" empty-icon="InboxIcon" :empty-text="`No ${kind} waiting.`">
          <template slot="thead">
            <vs-th>Date</vs-th><vs-th>{{ isInvoice ? 'Invoice' : 'Production' }}</vs-th><vs-th v-if="isInvoice">Customer</vs-th><vs-th>Last try</vs-th><vs-th></vs-th>
          </template>
          <template slot="row" slot-scope="{ r }">
            <vs-td>{{ date(r.day) }}</vs-td>
            <vs-td><a :href="opHref(r.id)" target="_blank">{{ r.ref || `#${r.id}` }}</a> <hv-pill v-if="r.held" tone="warn" label="Waiting confirmation" title="The run leaves it until &quot;Ready to be sent to SAGA&quot; is clicked on the invoice" /></vs-td>
            <vs-td v-if="isInvoice">{{ r.label || DASH }}</vs-td>
            <vs-td>
              <template v-if="r.last"><hv-pill :tone="itemTone(r.last.state)" :label="itemLabel(r.last.state)" :title="r.last.message || ''" /> <span class="hv2-muted">{{ roWhen(r.last.created_at, now) }}</span></template>
              <span v-else class="hv2-muted">never</span>
            </vs-td>
            <vs-td><vs-button size="small" type="flat" @click="$emit('test', r.id)">Test</vs-button></vs-td>
          </template>
        </hv-table>

        <div class="hv2-subhead mt-base">Last documents</div>
        <hv-table :rows="overview.recent" row-key="id" pagination :max-items="10" empty-icon="ClockIcon" empty-text="Nothing yet.">
          <template slot="thead">
            <vs-th>When</vs-th><vs-th>{{ isInvoice ? 'Invoice' : 'Production' }}</vs-th><vs-th>State</vs-th><vs-th>Detail</vs-th>
          </template>
          <template slot="row" slot-scope="{ r }">
            <vs-td class="hv2-nowrap">{{ roWhen(r.created_at, now) }}</vs-td>
            <vs-td><a :href="opHref(r.idoperation)" target="_blank">{{ r.ref || `#${r.idoperation}` }}</a></vs-td>
            <vs-td><hv-pill :tone="itemTone(r.state)" :label="itemLabel(r.state)" /><span v-if="r.dry_run" class="hv2-muted"> dry run</span></vs-td>
            <vs-td>{{ r.message }}</vs-td>
          </template>
        </hv-table>
      </template>
    </div>
  </div>
</template>

<script>
import axios from '@/axios.js'
import HvPill from '@/views/harvesting2/components/HvPill'
import HvTable from '@/views/harvesting2/components/HvTable'
import HvEmpty from '@/views/harvesting2/components/HvEmpty'
import formatMixin from '@/views/harvesting2/formatMixin'
import { roWhen } from '@/helpers/romaniaTime'

const ITEM = {
  sent: ['ok', 'In SAGA'],
  exists: ['info', 'Already in SAGA'],
  checked: ['info', 'Checked'],
  blocked: ['warn', 'Not sent'],
  error: ['danger', 'Refused by SAGA'],
  released: ['', 'Released']
}
const WAREHOUSES = { '0001': '0001 PRODUCTIE TEIU', '0002': '0002 MATERII PRIME TEIU', '0005': '0005 MARFURI' }
const POLL_MS = 3000
const POLL_MAX_MS = 15 * 60000

export default {
  components: { HvPill, HvTable, HvEmpty },
  mixins: [formatMixin],
  props: {
    service: { type: Object, required: true },
    tab: { type: String, required: true },
    overview: { type: Object, default: null },
    now: { type: Date, required: true },
    // An operation to open in the Test tab (from "Test" in Documents).
    openId: { type: Number, default: null }
  },
  data () {
    return {
      query: '',
      finding: false,
      findError: '',
      candidates: [],
      picked: null,
      preview: null,
      previewLoading: false,
      previewError: '',
      asking: false,
      activeRequest: null,
      pollTimer: null,
      pollStarted: 0,
      releaseReason: '',
      releasing: false
    }
  },
  computed: {
    isInvoice () { return this.service.key === 'sagax1n' },
    kind () { return this.isInvoice ? 'invoice' : 'production' },
    inSaga () { return !!(this.preview && this.preview.operation && this.preview.operation.issyncsaga) },
    // held = customer of saga_hold_customer, "Ready to be sent to SAGA" not clicked: the run leaves it.
    waitingRun () { return ((this.overview && this.overview.waiting) || []).filter(r => !r.held) },
    waitingConfirm () { return ((this.overview && this.overview.waiting) || []).filter(r => r.held) },
    // Only a test still waiting or running holds the buttons: once its result
    // is shown, "Send to SAGA" must work without reloading (seen 2026-10-21).
    requestBusy () { return !!(this.activeRequest && ['pending', 'running'].includes(this.activeRequest.status)) },
    h () { return (this.preview && this.preview.document && this.preview.document.header) || {} },
    lineRows () { return (this.preview && this.preview.document && this.preview.document.lines) || [] },
    bonRows () {
      const d = this.preview && this.preview.document
      if (!d || !d.bons) return []
      const out = []
      d.bons.forEach((b, i) => {
        out.push({ key: `b${i}`, out: false, ...b })
        b.consumptions.forEach((c, j) => out.push({ key: `b${i}c${j}`, out: true, ...c }))
      })
      return out
    }
  },
  watch: {
    openId: {
      immediate: true,
      handler (id) { if (id) this.pick(id) }
    },
    'service.key' () {
      this.stopPolling()
      Object.assign(this, { query: '', candidates: [], picked: null, preview: null, previewError: '', findError: '', activeRequest: null, releaseReason: '' })
    }
  },
  methods: {
    roWhen,
    errText (e) { return (e.response && e.response.data && e.response.data.message) || e.message },
    notify (title, text, color) {
      this.$vs.notify({ title, text, color, iconPack: 'feather', icon: color === 'danger' ? 'icon-alert-circle' : 'icon-check', position: 'bottom-right', time: 7000 })
    },
    opHref (id) { return `/web/${this.isInvoice ? 'invoice' : 'production'}/${id}` },
    warehouse (code) { return WAREHOUSES[code] || code },
    itemTone (st) { return (ITEM[st] || ['', st])[0] },
    itemLabel (st) { return (ITEM[st] || ['', st || '—'])[1] },
    requestTone (r) { return { done: 'ok', failed: 'danger', expired: 'warn', running: 'info', pending: '' }[r.status] || '' },
    requestLabel (r) { return { done: 'Done', failed: 'Failed', expired: 'Not picked up', running: 'Running', pending: 'Waiting' }[r.status] || r.status },
    requestTitle (r) {
      const what = r.action === 'send' ? 'Send to SAGA' : 'Check in SAGA'
      if (r.status === 'pending') return `${what}: waiting for the robot (it looks every minute)…`
      if (r.status === 'running') return `${what}: the robot is on it…`
      if (r.status === 'done') return `${what}: done.`
      if (r.status === 'expired') return `${what}: nobody picked it up. Is the robot running on the VPS?`
      return `${what}: failed.`
    },

    find () {
      const q = this.query.trim()
      if (!q) return
      this.finding = true
      this.findError = ''
      axios.get(`/sagasync/find/${this.service.key}?q=${encodeURIComponent(q)}`)
        .then((r) => {
          this.candidates = r.data || []
          if (!this.candidates.length) this.findError = `No ${this.kind} matches "${q}".`
          else if (this.candidates.length === 1) this.pick(this.candidates[0].id)
        })
        .catch((e) => { this.findError = this.errText(e) })
        .finally(() => { this.finding = false })
    },
    pick (id) {
      this.picked = id
      this.stopPolling()
      this.activeRequest = null
      this.releaseReason = ''
      this.loadPreview()
    },
    loadPreview () {
      if (!this.picked) return
      this.previewLoading = true
      this.previewError = ''
      axios.get(`/sagasync/preview/${this.service.key}/${this.picked}`)
        .then((r) => { this.preview = r.data })
        .catch((e) => { this.previewError = this.errText(e); this.preview = null })
        .finally(() => { this.previewLoading = false })
    },

    confirmSend () {
      const d = this.preview.document
      this.$vs.dialog({
        type: 'confirm',
        color: 'primary',
        title: `Send ${d.ref} to SAGA?`,
        text: this.isInvoice
          ? `The invoice is imported into SAGA (${d.currency}), not validated. After that, its customer, date, number, lines and VAT can no longer be changed in the ERP.`
          : `${d.bons.length} bon(s) are typed into SAGA, not validated. After that, the production's date, name and lines can no longer be changed in the ERP.`,
        acceptText: 'Send',
        cancelText: 'Cancel',
        accept: () => this.ask('send')
      })
    },
    ask (action) {
      this.asking = true
      axios.post('/sagasync/requests', { source: this.service.key, idoperation: this.picked, action })
        .then((r) => {
          this.activeRequest = { ...r.data, items: [] }
          this.startPolling()
          this.$emit('refresh')
        })
        .catch((e) => this.notify('Not asked', this.errText(e), 'danger'))
        .finally(() => { this.asking = false })
    },
    startPolling () {
      this.stopPolling()
      this.pollStarted = Date.now()
      this.pollTimer = setInterval(this.poll, POLL_MS)
    },
    stopPolling () {
      if (this.pollTimer) clearInterval(this.pollTimer)
      this.pollTimer = null
    },
    poll () {
      if (!this.activeRequest) return this.stopPolling()
      if (Date.now() - this.pollStarted > POLL_MAX_MS) return this.stopPolling()
      axios.get(`/sagasync/requests/${this.activeRequest.id}`)
        .then((r) => {
          this.activeRequest = r.data
          if (['done', 'failed', 'expired'].includes(r.data.status)) {
            this.stopPolling()
            this.loadPreview()
            this.$emit('refresh')
          }
        })
        .catch(() => {})
    },

    confirmRelease () {
      const d = this.preview.document
      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: `Release ${d.ref || `#${d.idoperation}`}?`,
        text: `Only if it was deleted in SAGA. The ERP will let it be changed again, and the next run will send it again.`,
        acceptText: 'Release',
        cancelText: 'Cancel',
        accept: this.release
      })
    },
    release () {
      this.releasing = true
      axios.post(`/sagasync/release/${this.picked}`, { reason: this.releaseReason.trim() })
        .then(() => {
          this.notify('Released', 'It can be changed again in the ERP, and will be sent again.', 'success')
          this.releaseReason = ''
          this.loadPreview()
          this.$emit('refresh')
        })
        .catch((e) => this.notify('Not released', this.errText(e), 'danger'))
        .finally(() => { this.releasing = false })
    }
  },
  beforeDestroy () {
    this.stopPolling()
  }
}
</script>

<style scoped>
.saga-find { display: flex; align-items: center; gap: 8px; max-width: 640px; margin-top: 8px; }
.saga-find-input { flex: 1; }
.saga-candidates { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 10px; }
.saga-candidate { display: flex; align-items: center; gap: 8px; padding: 6px 10px; border: 1px solid rgba(0, 0, 0, 0.12); border-radius: 6px; cursor: pointer; }
.saga-candidate.is-selected { border-color: rgba(var(--vs-primary), 1); background: rgba(var(--vs-primary), 0.06); }
.saga-doc-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 8px; flex-wrap: wrap; }
.saga-doc-ref { font-weight: 600; font-size: 1.1rem; }
.saga-out { padding-left: 18px; }
.saga-actions { margin-top: 14px; display: flex; align-items: center; flex-wrap: wrap; }
.saga-notes { margin: 10px 0 0; padding-left: 18px; color: rgba(var(--vs-warning), 1); }
.saga-history { display: flex; align-items: baseline; gap: 10px; padding: 4px 0; border-bottom: 1px solid rgba(0, 0, 0, 0.06); }
.saga-count { font-size: 1.6rem; font-weight: 600; }
</style>
